import { usePageContent } from "@/hooks/usePageContent";

const stats = [
  { value: "10,000+", label: "Acres Analyzed" },
  { value: "₹500 Cr+", label: "Value Unlocked" },
  { value: "48 Hrs", label: "Report Turnaround" },
];

export default function WealthMachineSection() {
  const { getContent } = usePageContent('homepage');
  const heading = getContent('home-wealth-heading') || 'Your Land is a Wealth Machine \u2014 Let\'s Turn It On';

  return (
    <section className="relative min-h-screen flex items-center bg-background-100 pt-20 overflow-hidden">
      {/* Ambient glows */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-primary-500/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/3 w-64 h-64 rounded-full bg-accent-500/5 blur-[80px] pointer-events-none" />

      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left text */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary-500/30 bg-primary-500/10">
              <i className="ri-flashlight-line text-primary-600 text-sm" />
              <span className="text-primary-600 text-sm font-medium">AI-Powered Land Monetization</span>
            </div>
            <h2 className="text-5xl lg:text-6xl xl:text-7xl font-display font-bold leading-[1.1] text-foreground-950">
              {heading}
            </h2>
            <p className="text-foreground-600 text-lg max-w-lg leading-relaxed">
              CashMyProperty.ai uses advanced algorithms to calculate the{" "}
              <strong className="text-foreground-950">Highest &amp; Best Use</strong> of your land,
              connecting you with developers and brands without losing ownership.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => {
                  const el = document.querySelector("#contact");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get AI Feasibility Report
                <i className="ri-file-chart-line" />
              </button>
              <button
                onClick={() => {
                  const el = document.querySelector("#contact");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-500/40 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                Talk to a Strategist
              </button>
            </div>
            <div className="flex items-center gap-6 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line text-primary-500" />
                No Blind Deals
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-lock-line text-primary-500" />
                100% Data Privacy
              </span>
            </div>
          </div>

          {/* Right: animated AI card */}
          <div className="hidden lg:flex justify-center items-center relative">
            <div className="relative w-80 h-80 animate-float">
              {/* Back card */}
              <div className="absolute inset-0 rounded-2xl border border-primary-800/20 bg-background-200/50 backdrop-blur-sm transform rotate-12 translate-x-4" />
              {/* Main card */}
              <div className="absolute inset-0 rounded-2xl border border-primary-700/30 bg-background-200/80 backdrop-blur-sm transform -rotate-6">
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-accent-500" />
                    <div className="w-3 h-3 rounded-full bg-primary-500" />
                  </div>
                  <div className="text-xs text-foreground-500 font-mono">AI Analyzing...</div>
                  <div className="space-y-3">
                    {[
                      { label: "Zoning Data", pct: "85%", color: "bg-primary-500" },
                      { label: "Footfall Analysis", pct: "70%", color: "bg-primary-500 animate-pulse" },
                      { label: "Market Rates", pct: "45%", color: "bg-accent-500" },
                      { label: "HBU Scoring", pct: "60%", color: "bg-secondary-500" },
                    ].map((item) => (
                      <div key={item.label}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-foreground-500">● {item.label}</span>
                          <span className="text-primary-600">✓</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-background-400 overflow-hidden">
                          <div className={`h-full rounded-full ${item.color}`} style={{ width: item.pct }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 rounded-lg bg-primary-500/10 border border-primary-700/30">
                    <div className="text-xs text-primary-600 font-medium">Recommendation</div>
                    <div className="text-sm text-foreground-950 font-semibold mt-1">Commercial Lease — QSR</div>
                    <div className="text-xs text-accent-500 mt-0.5">ROI: 18% p.a. projected</div>
                  </div>
                </div>
              </div>
              {/* Glow dots */}
              <div className="absolute -top-3 -right-3 w-5 h-5 rounded-full bg-primary-500 animate-pulse-glow" />
              <div className="absolute -bottom-2 -left-2 w-3 h-3 rounded-full bg-accent-500 animate-pulse-glow" />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-16 p-6 rounded-2xl bg-background-200/60 border border-primary-900/30 backdrop-blur-sm">
          <div className="grid grid-cols-3 divide-x divide-primary-900/30">
            {stats.map((s) => (
              <div key={s.label} className="text-center px-4">
                <div className="text-2xl md:text-3xl font-display font-bold text-foreground-950">{s.value}</div>
                <div className="text-sm text-foreground-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}